import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Building2, Plus, Search, Globe, Users, DollarSign, TrendingUp,
  ArrowLeft, RefreshCw, Settings, Pause, Play, Trash2, ExternalLink,
  BarChart3, Palette, Shield, ChevronRight, X, Check, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: 'bg-emerald-500/20 text-emerald-300',
  trial: 'bg-amber-500/20 text-amber-300',
  suspended: 'bg-red-500/20 text-red-300',
  deleted: 'bg-slate-500/20 text-slate-300',
};

const TIER_LABELS = {
  starter: { label: 'Starter', color: 'text-blue-300', price: '$299/mo' },
  professional: { label: 'Professional', color: 'text-violet-300', price: '$799/mo' },
  enterprise: { label: 'Enterprise', color: 'text-amber-300', price: '$2,499/mo' },
};

function OverviewStats({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: 'Total Tenants', value: stats.total_tenants, icon: Building2, color: 'blue' },
    { label: 'Active', value: stats.active_tenants, icon: Check, color: 'green' },
    { label: 'MRR', value: `$${(stats.mrr || 0).toLocaleString()}`, icon: DollarSign, color: 'violet' },
    { label: 'ARR', value: `$${(stats.arr || 0).toLocaleString()}`, icon: TrendingUp, color: 'amber' },
    { label: 'On Trial', value: stats.trial_tenants, icon: Eye, color: 'cyan' },
    { label: 'Tenant Users', value: stats.total_tenant_users, icon: Users, color: 'emerald' },
  ];
  const colorMap = {
    blue: 'from-blue-600/20 to-blue-700/10 border-blue-500/30 text-blue-400',
    green: 'from-emerald-600/20 to-emerald-700/10 border-emerald-500/30 text-emerald-400',
    violet: 'from-violet-600/20 to-violet-700/10 border-violet-500/30 text-violet-400',
    amber: 'from-amber-600/20 to-amber-700/10 border-amber-500/30 text-amber-400',
    cyan: 'from-cyan-600/20 to-cyan-700/10 border-cyan-500/30 text-cyan-400',
    emerald: 'from-emerald-600/20 to-emerald-700/10 border-emerald-500/30 text-emerald-400',
  };
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map(c => (
        <div key={c.label} className={`bg-gradient-to-br ${colorMap[c.color]} border rounded-xl p-4`} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
          <c.icon className={`w-4 h-4 mb-2 ${colorMap[c.color].split(' ').pop()}`} />
          <p className="text-xl font-bold text-white">{c.value}</p>
          <p className="text-xs text-slate-300 mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function CreateTenantModal({ onClose, onCreated, token }) {
  const [form, setForm] = useState({
    name: '', subdomain: '', admin_email: '', admin_name: '',
    admin_password: 'changeme123', license_tier: 'starter',
    organization_type: 'school_district', primary_color: '#3b82f6',
    tagline: '', start_trial: true, notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/tenants`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Tenant "${form.name}" created!`);
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/60 backdrop-blur-sm" data-testid="create-tenant-modal">
      <div className="bg-[#111827] border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Create New Tenant</h2>
          <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Organization Name *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} required
                placeholder="Lincoln High School" className="bg-slate-900 border-slate-700 text-white" data-testid="tenant-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Subdomain *</Label>
              <div className="flex items-center gap-1">
                <Input value={form.subdomain} onChange={e => set('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} required
                  placeholder="lincoln-hs" className="bg-slate-900 border-slate-700 text-white" data-testid="tenant-subdomain-input" />
                <span className="text-xs text-slate-300 whitespace-nowrap">.futurepath.ai</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Organization Type</Label>
              <select value={form.organization_type} onChange={e => set('organization_type', e.target.value)}
                className="w-full bg-slate-900 text-white text-sm rounded-md px-3 py-2 border border-slate-700">
                <option value="school_district">School District</option>
                <option value="university">University</option>
                <option value="career_center">Career Center</option>
                <option value="workforce_agency">Workforce Agency</option>
                <option value="employer">Employer</option>
                <option value="nonprofit">Nonprofit</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">License Tier</Label>
              <select value={form.license_tier} onChange={e => set('license_tier', e.target.value)}
                className="w-full bg-slate-900 text-white text-sm rounded-md px-3 py-2 border border-slate-700" data-testid="tenant-tier-select">
                <option value="starter">Starter (100 users, $299/mo)</option>
                <option value="professional">Professional (500 users, $799/mo)</option>
                <option value="enterprise">Enterprise (10,000 users, $2,499/mo)</option>
              </select>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4">
            <p className="text-sm text-slate-300 mb-3">Tenant Admin Account</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Admin Name</Label>
                <Input value={form.admin_name} onChange={e => set('admin_name', e.target.value)}
                  placeholder="Jane Smith" className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Admin Email</Label>
                <Input value={form.admin_email} onChange={e => set('admin_email', e.target.value)} type="email"
                  placeholder="admin@lincoln.edu" className="bg-slate-900 border-slate-700 text-white" data-testid="tenant-admin-email" />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4">
            <p className="text-sm text-slate-300 mb-3">Branding</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" />
                  <Input value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Tagline</Label>
                <Input value={form.tagline} onChange={e => set('tagline', e.target.value)}
                  placeholder="Empowering Student Futures" className="bg-slate-900 border-slate-700 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300 text-sm">Notes</Label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
              className="w-full bg-slate-900 text-white text-sm rounded-md px-3 py-2 border border-slate-700"
              placeholder="Internal notes about this tenant..." />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.start_trial} onChange={e => set('start_trial', e.target.checked)}
              className="w-4 h-4 rounded" />
            <span className="text-sm text-slate-300">Start subscription immediately</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-tenant-submit">
              {loading ? 'Creating...' : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TenantDetailPanel({ tenant, token, onClose, onUpdate }) {
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!tenant) return;
    fetch(`${API}/tenants/${tenant.id}/analytics`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(setAnalytics).catch(() => {});
  }, [tenant, token]);

  if (!tenant) return null;

  const handleAction = async (action) => {
    const url = `${API}/tenants/${tenant.id}/${action}`;
    try {
      const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Tenant ${action}d`);
      onUpdate();
    } catch { toast.error(`Failed to ${action}`); }
  };

  const b = tenant.branding || {};
  const tierInfo = TIER_LABELS[tenant.license_tier] || TIER_LABELS.starter;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0a1729]/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#111827] border-l border-slate-700 h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {b.logo_url ? (
                <img src={b.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: b.primary_color || '#3b82f6' }}>
                  <Building2 className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-white">{tenant.name}</h2>
                <p className="text-xs text-slate-300">{tenant.subdomain}.futurepath.ai</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex gap-2 mt-4">
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[tenant.status]}`}>{tenant.status}</span>
            <span className={`text-xs px-2 py-1 rounded-full bg-slate-700/50 ${tierInfo.color}`}>{tierInfo.label}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-slate-700/50 text-slate-300">{tierInfo.price}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          {['overview', 'branding', 'actions'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 capitalize ${tab === t ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-300 hover:text-slate-300'}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5">
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{analytics?.user_count || tenant.user_count || 0}</p>
                  <p className="text-xs text-slate-300">Users</p>
                  <p className="text-xs text-slate-300 mt-1">of {tenant.max_users} max</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{analytics?.usage_percentage || 0}%</p>
                  <p className="text-xs text-slate-300">Capacity Used</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{analytics?.ai_actions_total || 0}</p>
                  <p className="text-xs text-slate-300">AI Actions</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{analytics?.goals_created || 0}</p>
                  <p className="text-xs text-slate-300">Goals Created</p>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-slate-300">Details</p>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-slate-300">Admin Email</span>
                  <span className="text-slate-300">{tenant.admin_email || '-'}</span>
                  <span className="text-slate-300">Org Type</span>
                  <span className="text-slate-300 capitalize">{(tenant.organization_type || '').replace('_', ' ')}</span>
                  <span className="text-slate-300">Created</span>
                  <span className="text-slate-300">{tenant.created_at?.slice(0, 10)}</span>
                  <span className="text-slate-300">Custom Domain</span>
                  <span className="text-slate-300">{tenant.custom_domain || 'Not set'}</span>
                  <span className="text-slate-300">Trial Ends</span>
                  <span className="text-slate-300">{tenant.trial_ends_at?.slice(0, 10) || 'N/A'}</span>
                </div>
              </div>

              {tenant.notes && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-300 mb-1">Notes</p>
                  <p className="text-sm text-slate-300">{tenant.notes}</p>
                </div>
              )}

              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-300 mb-2">Enabled Features</p>
                <div className="flex flex-wrap gap-2">
                  {(tenant.enabled_features || []).map(f => (
                    <span key={f} className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300">{f}</span>
                  ))}
                </div>
              </div>
            </>
          )}

          {tab === 'branding' && (
            <>
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-slate-300">Brand Preview</p>
                <div className="rounded-lg overflow-hidden border border-slate-700">
                  <div className="p-4" style={{ background: `linear-gradient(135deg, ${b.primary_color || '#3b82f6'}, ${b.secondary_color || '#1e3a5f'})` }}>
                    <p className="text-white font-semibold">{b.app_name || tenant.name}</p>
                    <p className="text-white/70 text-xs">{b.tagline}</p>
                  </div>
                  <div className="p-3 bg-[#0a1628]">
                    <div className="flex gap-2">
                      <div className="w-20 h-2 rounded" style={{ background: b.primary_color || '#3b82f6' }} />
                      <div className="w-12 h-2 rounded" style={{ background: b.accent_color || '#06b6d4' }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[['Primary', b.primary_color], ['Secondary', b.secondary_color], ['Accent', b.accent_color]].map(([label, color]) => (
                  <div key={label} className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="w-8 h-8 rounded-full mx-auto mb-1" style={{ background: color || '#666' }} />
                    <p className="text-xs text-slate-300">{label}</p>
                    <p className="text-xs text-slate-300">{color}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'actions' && (
            <div className="space-y-3">
              {tenant.status === 'active' && (
                <button onClick={() => handleAction('suspend')}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-left">
                  <Pause className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-sm font-medium text-red-300">Suspend Tenant</p>
                    <p className="text-xs text-slate-300">Blocks all user access immediately</p>
                  </div>
                </button>
              )}
              {tenant.status === 'suspended' && (
                <button onClick={() => handleAction('activate')}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-left">
                  <Play className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-emerald-300">Re-Activate Tenant</p>
                    <p className="text-xs text-slate-300">Restores full access for all users</p>
                  </div>
                </button>
              )}
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete "${tenant.name}"? This is a soft delete, data is preserved but access is revoked.`)) return;
                  try {
                    await fetch(`${API}/tenants/${tenant.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Tenant deleted');
                    onUpdate(); onClose();
                  } catch { toast.error('Delete failed'); }
                }}
                className="w-full flex items-center gap-3 p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:bg-red-500/10 hover:border-red-500/30 text-left">
                <Trash2 className="w-5 h-5 text-slate-300" />
                <div>
                  <p className="text-sm font-medium text-slate-300">Delete Tenant</p>
                  <p className="text-xs text-slate-300">Soft delete, data preserved, access revoked</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MasterAdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    try {
      const [ov, tn] = await Promise.all([
        fetch(`${API}/tenants/overview/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/tenants${search ? `?search=${search}` : ''}`, { headers }).then(r => r.json())
      ]);
      setOverview(ov);
      setTenants(tn.tenants || []);
    } catch (e) {
      toast.error('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') { navigate('/login'); return; }
    fetchAll();
  }, []);

  useEffect(() => { const t = setTimeout(fetchAll, 300); return () => clearTimeout(t); }, [search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="master-admin-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1b30]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-slate-300 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold">Master Admin Portal</h1>
                <p className="text-xs text-slate-300">Tenant & License Management</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={fetchAll} data-testid="refresh-tenants-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-tenant-btn">
              <Plus className="w-4 h-4 mr-2" /> New Tenant
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Overview */}
        <OverviewStats stats={overview} />

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              placeholder="Search tenants by name, subdomain, or email..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-white"
              data-testid="tenant-search-input"
            />
          </div>
          <span className="text-sm text-slate-300">{tenants.length} tenants</span>
        </div>

        {/* Tenant Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map(t => {
            const b = t.branding || {};
            const tier = TIER_LABELS[t.license_tier] || TIER_LABELS.starter;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTenant(t)}
                className="text-left bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-slate-600 transition-all group"
                data-testid={`tenant-card-${t.subdomain}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: b.primary_color || '#3b82f6' }}>
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">{t.name}</p>
                      <p className="text-xs text-slate-300">{t.subdomain}.futurepath.ai</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-300" />
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[t.status]}`}>{t.status}</span>
                  <span className={`text-xs ${tier.color}`}>{tier.label}</span>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.user_count || 0} users</span>
                  <span>{tier.price}</span>
                </div>
              </button>
            );
          })}

          {tenants.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-300">No tenants yet</p>
              <Button size="sm" onClick={() => setShowCreate(true)} className="mt-4 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Create First Tenant
              </Button>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateTenantModal
          token={token}
          onClose={() => setShowCreate(false)}
          onCreated={() => fetchAll()}
        />
      )}

      {selectedTenant && (
        <TenantDetailPanel
          tenant={selectedTenant}
          token={token}
          onClose={() => setSelectedTenant(null)}
          onUpdate={() => { fetchAll(); setSelectedTenant(null); }}
        />
      )}
    </div>
  );
}
